import DOMPurify from "dompurify";

export const getCommerceItems = (eventData) =>
  (eventData?.additionalFields || []).filter(
    (field) => field?.fieldType === "CommerceItem",
  );

export const isCommerceItemAvailabilityHidden = (ticket) =>
  ticket?.hideAvailableQuantity !== false;

export const getPublicCommerceItemAvailabilityLabel = (ticket) => {
  if (ticket?.soldOut) return "SOLD OUT";
  if (isCommerceItemAvailabilityHidden(ticket)) return "";
  if (ticket?.availableQuantity == null) return "";
  return `${ticket.availableQuantity} AVAILABLE`;
};

export const isTestInlinePaymentEvent = (eventData) =>
  eventData?.prodPayment !== true &&
  eventData?.paymentType === "payment" &&
  getCommerceItems(eventData).length > 0;

export const ticketLabelToText = (value) => {
  if (!value) return "";

  const normalized = String(value)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p\s*>/gi, "\n")
    .replace(/<\/li\s*>/gi, "\n");

  if (typeof document !== "undefined") {
    const container = document.createElement("div");
    container.innerHTML = DOMPurify.sanitize(normalized, {
      ALLOWED_TAGS: [],
    });
    return (container.textContent || "")
      .replace(/\u00a0/g, " ")
      .replace(/[ \t]+/g, " ")
      .replace(/\n\s*\n/g, "\n")
      .trim();
  }

  return normalized.replace(/<[^>]*>/g, "").trim();
};

export const formatTicketCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "Not configured";

  return new Intl.NumberFormat("en-US", {
    currency: "USD",
    style: "currency",
  }).format(amount);
};

const toCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
};

const toConfiguredNumber = (value) => {
  if (value === "" || value == null) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const CAPACITY_CONSUMING_STATUSES = new Set([
  "Registered",
  "Checked-In",
  "Pending",
  "Completed",
]);

const toQuantity = (value) => Math.max(Math.floor(toCount(value)), 0);

const getRegistrationQuantity = (registration, fieldName) =>
  toQuantity(registration?.[fieldName]);

const registrationConsumesCapacity = (registration) =>
  Boolean(registration) && CAPACITY_CONSUMING_STATUSES.has(registration.status);

export const getCommerceItemSelectableMax = (
  ticket,
  {
    tickets = [],
    eventData = {},
    soldCounts = {},
    eventSoldCount = 0,
    originalRegistration = null,
    registration = {},
  } = {},
) => {
  const fieldName = ticket?.fieldName;
  const currentQuantity = getRegistrationQuantity(registration, fieldName);
  const configuredMaximum = toConfiguredNumber(ticket?.fieldMax);
  const originalConsumesCapacity = registrationConsumesCapacity(
    originalRegistration,
  );
  const existingAllocation = originalConsumesCapacity ? currentQuantity : 0;
  const preservedMaximum = Math.max(
    configuredMaximum == null ? 0 : toQuantity(configuredMaximum),
    existingAllocation,
  );

  if (ticket?.fieldCapacity === "itemCapacity") {
    const capacity = toConfiguredNumber(ticket.fieldCapacityNum);
    if (capacity == null) return preservedMaximum;

    const originalQuantity = originalConsumesCapacity
      ? getRegistrationQuantity(originalRegistration, fieldName)
      : 0;
    const availableToRegistration = Math.max(
      toQuantity(capacity) - toQuantity(soldCounts[fieldName]) + originalQuantity,
      0,
    );

    return Math.max(
      Math.min(preservedMaximum, availableToRegistration),
      existingAllocation,
    );
  }

  if (ticket?.fieldCapacity === "eventCapacity") {
    const capacity = toConfiguredNumber(eventData?.capacity);
    if (capacity == null) return preservedMaximum;

    const eventCapacityTickets = tickets.filter(
      (candidate) => candidate?.fieldCapacity === "eventCapacity",
    );
    const originalEventQuantity = originalConsumesCapacity
      ? eventCapacityTickets.reduce(
          (total, candidate) =>
            total +
            getRegistrationQuantity(originalRegistration, candidate.fieldName),
          0,
        )
      : 0;
    const otherSelectedQuantity = eventCapacityTickets.reduce(
      (total, candidate) =>
        candidate.fieldName === fieldName
          ? total
          : total + getRegistrationQuantity(registration, candidate.fieldName),
      0,
    );
    const availableToTicket = Math.max(
      toQuantity(capacity) -
        toQuantity(eventSoldCount) +
        originalEventQuantity -
        otherSelectedQuantity,
      0,
    );

    return Math.max(
      Math.min(preservedMaximum, availableToTicket),
      existingAllocation,
    );
  }

  return preservedMaximum;
};

export const getTicketAvailability = (
  ticket,
  soldCount = 0,
  eventData = {},
  eventSoldCount = 0,
) => {
  if (ticket?.fieldCapacity === "itemCapacity") {
    const capacity = toConfiguredNumber(ticket.fieldCapacityNum);
    return {
      capacity,
      remaining: capacity != null
        ? Math.max(capacity - toCount(soldCount), 0)
        : null,
      scope: "ticket",
      sold: toCount(soldCount),
    };
  }

  if (ticket?.fieldCapacity === "eventCapacity") {
    const capacity = toConfiguredNumber(eventData?.capacity);
    return {
      capacity,
      remaining: capacity != null
        ? Math.max(capacity - toCount(eventSoldCount), 0)
        : null,
      scope: "event",
      sold: toCount(soldCount),
    };
  }

  return {
    capacity: null,
    remaining: null,
    scope: "unlimited",
    sold: toCount(soldCount),
  };
};

const getRuleNames = (ticket, propertyName, ticketNames) =>
  (Array.isArray(ticket?.[propertyName]) ? ticket[propertyName] : [])
    .filter(Boolean)
    .map((fieldName) => ticketNames.get(fieldName) || fieldName);

export const buildTicketConfigurationText = (
  eventData,
  soldCounts = {},
  eventSoldCount = 0,
) => {
  const tickets = getCommerceItems(eventData);
  const ticketNames = new Map(
    tickets.map((ticket, index) => [
      ticket.fieldName,
      ticketLabelToText(ticket.fieldLabel) ||
        ticket.fieldName ||
        `Ticket ${index + 1}`,
    ]),
  );
  const paymentType = eventData?.paymentType || "disabled";
  const lines = [
    eventData?.name || eventData?.sysname || "Event",
    "Ticket configuration",
    `Payment: ${paymentType} (${eventData?.prodPayment ? "production" : "test"}); tax ${Number(eventData?.paymentTax || 0)}%`,
    "",
  ];

  if (!tickets.length) {
    lines.push("No commerce-item tickets are configured.");
    return lines.join("\n");
  }

  tickets.forEach((ticket, index) => {
    const name = ticketNames.get(ticket.fieldName) || `Ticket ${index + 1}`;
    const sold = soldCounts[ticket.fieldName] || 0;
    const availability = getTicketAvailability(
      ticket,
      sold,
      eventData,
      eventSoldCount,
    );
    const showRules = getRuleNames(
      ticket,
      "commerceItemPrerequisites",
      ticketNames,
    );
    const hideRules = getRuleNames(
      ticket,
      "commerceItemHidePrerequisites",
      ticketNames,
    );
    const minimum = ticket.fieldMin === "" || ticket.fieldMin == null
      ? 0
      : ticket.fieldMin;
    const maximum = ticket.fieldMax === "" || ticket.fieldMax == null
      ? "not configured"
      : ticket.fieldMax;

    lines.push(`${index + 1}. ${name}`);
    lines.push(`   Field: ${ticket.fieldName || "not configured"}`);
    lines.push(`   Price: ${formatTicketCurrency(ticket.fieldCost)}`);
    lines.push(`   Quantity per registration: ${minimum}-${maximum}`);
    lines.push(`   Sold: ${availability.sold}`);

    if (availability.scope === "ticket") {
      lines.push(
        `   Capacity: ${availability.capacity ?? "not configured"} ticket total; ${availability.remaining ?? "unknown"} remaining`,
      );
    } else if (availability.scope === "event") {
      lines.push(
        `   Capacity: shared event capacity ${availability.capacity ?? "not configured"}; ${availability.remaining ?? "unknown"} event spaces remaining`,
      );
    } else {
      lines.push("   Capacity: unlimited");
    }

    lines.push(
      `   Visibility: ${showRules.length ? `show after any of ${showRules.join(", ")}` : "shown by default"}; ${hideRules.length ? `hide after any of ${hideRules.join(", ")}` : "no hide rules"}`,
    );
    lines.push(
      `   Individual item check-in: ${ticket.fieldCheckIn ? "enabled" : "disabled"}`,
    );
    lines.push("");
  });

  return lines.join("\n").trim();
};
